import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";

import adminFetch from "../../utils/adminFetch";

const API = `${process.env.REACT_APP_API_URL}/api/testimonials/admin`;

function autoInitials(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_COLORS = [
  "#2563EB", "#B45309", "#7C3AED", "#065F46",
  "#BE185D", "#0F766E", "#92400E", "#1D4ED8",
];

const EMPTY_FORM = {
  name: "", initials: "", avatarBg: "#2563EB",
  rating: 5, text: "", timeLabel: "", isPublished: true, displayOrder: 0,
};

// ── Spinner ───────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "240px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

// ── Stars ─────────────────────────────────────────────────────────────
function Stars({ count, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button"
          onClick={() => onChange?.(n)}
          onMouseEnter={() => onChange && setHovered(n)}
          onMouseLeave={() => onChange && setHovered(0)}
          className={onChange ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}>
          <svg width="16" height="16" viewBox="0 0 24 24"
            fill={n <= (hovered || count) ? "#F59E0B" : "none"}
            stroke={n <= (hovered || count) ? "#F59E0B" : "#D1D5DB"} strokeWidth="1.5">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      ))}
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────
function Avatar({ initials, bg, size = "md" }) {
  const sizes = {
    sm: { width: "32px", height: "32px", fontSize: "9px" },
    md: { width: "40px", height: "40px", fontSize: "11px" },
    lg: { width: "48px", height: "48px", fontSize: "13px" },
  };
  const s = sizes[size] || sizes.md;
  return (
    <div style={{
      width: s.width, height: s.height,
      borderRadius: "50%", backgroundColor: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, boxShadow: "0 0 0 2px #fff, 0 1px 4px rgba(0,0,0,0.1)",
    }}>
      <span style={{
        fontFamily: "'Cinzel', serif", fontWeight: 700,
        color: "#fff", fontSize: s.fontSize,
      }}>
        {initials || "??"}
      </span>
    </div>
  );
}

// ── Confirmation / Alert Modal ────────────────────────────────────────
function ConfirmModal({ open, type = "confirm", title, message, confirmLabel = "Confirm", cancelLabel = "Cancel", onConfirm, onCancel }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const iconMap = {
    delete: (
      <div style={{ width: "48px", height: "48px", borderRadius: "50%", backgroundColor: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v6M14 11v6M9 6V4h6v2" />
        </svg>
      </div>
    ),
    warning: (
      <div style={{ width: "48px", height: "48px", borderRadius: "50%", backgroundColor: "#FFFBEB", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
    ),
    error: (
      <div style={{ width: "48px", height: "48px", borderRadius: "50%", backgroundColor: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
    ),
  };

  const confirmBgMap = {
    delete:  "#EF4444",
    warning: "#F59E0B",
    error:   "#012E72",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 60,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
    }}>
      <div
        style={{ position: "absolute", inset: 0, backgroundColor: "rgba(1,4,20,0.45)", backdropFilter: "blur(4px)" }}
        onClick={onCancel}
      />
      <div style={{
        position: "relative", backgroundColor: "#fff",
        borderRadius: "20px", boxShadow: "0 32px 80px rgba(1,46,114,0.22)",
        width: "100%", maxWidth: "380px", padding: "32px 28px",
        textAlign: "center", animation: "modal-in 0.18s ease-out both",
      }}>
        {iconMap[type] || iconMap.warning}
        <h3 style={{
          fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "14px",
          letterSpacing: "0.06em", textTransform: "uppercase", color: "#012E72", marginBottom: "10px",
        }}>
          {title}
        </h3>
        <p style={{
          fontFamily: "'Constantia', serif", fontSize: "13px",
          color: "#6B7280", lineHeight: 1.7, marginBottom: "24px",
        }}>
          {message}
        </p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          {onCancel && (
            <button
              onClick={onCancel}
              style={{
                fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
                textTransform: "uppercase", padding: "9px 20px", borderRadius: "999px",
                color: "#6B7280", border: "1px solid rgba(107,114,128,0.25)",
                backgroundColor: "transparent", cursor: "pointer",
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = "#F9FAFB"}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={onConfirm}
            style={{
              fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
              textTransform: "uppercase", padding: "9px 20px", borderRadius: "999px",
              color: "#fff", backgroundColor: confirmBgMap[type] || confirmBgMap.warning,
              border: "none", cursor: "pointer",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Form ──────────────────────────────────────────────────────────────
function TestimonialForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [autoInit, setAutoInit] = useState(
    !initial?.initials || initial.initials === autoInitials(initial.name)
  );
  const [dirty, setDirty] = useState(false);
  const [discardModal, setDiscardModal] = useState(false);

  const set = (key, val) => {
    setDirty(true);
    setForm((f) => ({ ...f, [key]: val }));
  };

  const handleName = (val) => {
    setDirty(true);
    setForm((f) => ({
      ...f,
      name: val,
      initials: autoInit ? autoInitials(val) : f.initials,
    }));
  };

  const handleInitials = (val) => {
    setAutoInit(false);
    setDirty(true);
    setForm((f) => ({ ...f, initials: val.toUpperCase().slice(0, 3) }));
  };

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };

  const handleCancel = () => {
    if (dirty) setDiscardModal(true);
    else onCancel();
  };

  const inputStyle = {
    width: "100%", fontFamily: "'Constantia', serif", fontSize: "14px",
    color: "#012E72", backgroundColor: "#fff",
    border: "1px solid rgba(1,46,114,0.13)", borderRadius: "12px",
    padding: "10px 14px", outline: "none", boxSizing: "border-box",
    transition: "border-color 0.15s",
  };

  const labelStyle = {
    display: "block", fontFamily: "'Cinzel', serif", fontSize: "8px",
    letterSpacing: "0.22em", textTransform: "uppercase",
    color: "rgba(1,46,114,0.5)", marginBottom: "8px",
  };

  return (
    <>
      <ConfirmModal
        open={discardModal}
        type="warning"
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={() => { setDiscardModal(false); onCancel(); }}
        onCancel={() => setDiscardModal(false)}
      />

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
          <div>
            <label style={labelStyle}>Reviewer Name <span style={{ color: "#F87171" }}>*</span></label>
            <input
              required value={form.name} onChange={(e) => handleName(e.target.value)}
              placeholder="e.g. Maria Santos" style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#002DB5"}
              onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"}
            />
          </div>
          <div>
            <label style={labelStyle}>
              Initials <span style={{ fontFamily: "'Constantia', serif", textTransform: "none", letterSpacing: 0, color: "rgba(1,46,114,0.3)", fontSize: "11px" }}>(auto-filled)</span>
            </label>
            <input
              value={form.initials} onChange={(e) => handleInitials(e.target.value)}
              placeholder="MS" maxLength={3} style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#002DB5"}
              onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"}
            />
          </div>
          <div>
            <label style={labelStyle}>Time Label</label>
            <input
              value={form.timeLabel} onChange={(e) => set("timeLabel", e.target.value)}
              placeholder="e.g. a month ago" style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#002DB5"}
              onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"}
            />
          </div>
          <div>
            <label style={labelStyle}>
              Display Order <span style={{ fontFamily: "'Constantia', serif", textTransform: "none", letterSpacing: 0, color: "rgba(1,46,114,0.3)", fontSize: "11px" }}>(lower = first)</span>
            </label>
            <input
              type="number" value={form.displayOrder}
              onChange={(e) => set("displayOrder", Number(e.target.value))}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#002DB5"}
              onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"}
            />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Review Text <span style={{ color: "#F87171" }}>*</span></label>
          <textarea
            required rows={4} value={form.text}
            onChange={(e) => set("text", e.target.value)}
            placeholder="Paste the review text here…"
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.7 }}
            onFocus={e => e.target.style.borderColor = "#002DB5"}
            onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"}
          />
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "flex-start" }}>
          {/* Rating */}
          <div>
            <label style={labelStyle}>Rating</label>
            <Stars count={form.rating} onChange={(n) => set("rating", n)} />
          </div>

          {/* Avatar color */}
          <div>
            <label style={labelStyle}>Avatar Color</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {AVATAR_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => set("avatarBg", c)}
                  style={{
                    width: "28px", height: "28px", borderRadius: "50%",
                    backgroundColor: c, border: "none", cursor: "pointer",
                    outline: form.avatarBg === c ? "2.5px solid #012E72" : "none",
                    outlineOffset: "2px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "transform 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.12)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                >
                  {form.avatarBg === c && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                      stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <label style={labelStyle}>Preview</label>
            <Avatar initials={form.initials} bg={form.avatarBg} size="lg" />
          </div>

          {/* Published toggle */}
          <div>
            <label style={labelStyle}>Published</label>
            <button
              type="button"
              onClick={() => set("isPublished", !form.isPublished)}
              style={{
                position: "relative", display: "inline-flex",
                height: "24px", width: "44px", alignItems: "center",
                borderRadius: "999px", border: "none", cursor: "pointer",
                backgroundColor: form.isPublished ? "#012E72" : "#D1D5DB",
                transition: "background-color 0.2s",
              }}
            >
              <span style={{
                display: "inline-block", width: "16px", height: "16px",
                borderRadius: "50%", backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transform: form.isPublished ? "translateX(22px)" : "translateX(4px)",
                transition: "transform 0.2s",
              }} />
            </button>
          </div>
        </div>

        {/* Footer buttons */}
        <div style={{
          display: "flex", gap: "10px", paddingTop: "16px",
          borderTop: "1px solid rgba(1,46,114,0.08)",
        }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
              textTransform: "uppercase", color: "#fff",
              background: "linear-gradient(135deg, #012E72, #002DB5)",
              border: "none", borderRadius: "999px",
              padding: "9px 24px", cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
              boxShadow: "0 4px 16px rgba(0,45,181,0.25)",
              display: "flex", alignItems: "center", gap: "8px",
            }}
          >
            {saving && (
              <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
            {saving ? "Saving…" : initial ? "Save Changes" : "Add Testimonial"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em",
              textTransform: "uppercase", color: "#6B7280",
              border: "1px solid rgba(107,114,128,0.25)", borderRadius: "999px",
              padding: "9px 20px", cursor: "pointer", backgroundColor: "transparent",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = "#F9FAFB"}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}

// ── Add Testimonial Modal ─────────────────────────────────────────────
function AddTestimonialModal({ open, onSave, onClose, saving }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px", backgroundColor: "rgba(1,4,20,0.5)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: "620px",
          backgroundColor: "#F7F4EF", borderRadius: "20px",
          overflow: "hidden", boxShadow: "0 32px 80px rgba(1,46,114,0.22)",
          border: "1px solid rgba(1,46,114,0.1)",
          maxHeight: "90vh", display: "flex", flexDirection: "column",
          animation: "modal-in 0.18s ease-out both",
        }}
      >
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #012E72, #002DB5)",
          padding: "22px 28px",
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", gap: "12px", flexShrink: 0,
        }}>
          <div>
            <p style={{
              fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.28em",
              textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: "5px",
            }}>
              Testimonials
            </p>
            <h2 style={{
              fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px",
              color: "#fff", letterSpacing: "0.04em", lineHeight: 1.2,
            }}>
              New Testimonial
            </h2>
            <p style={{
              fontFamily: "'Constantia', serif", fontSize: "13px",
              color: "rgba(255,255,255,0.5)", marginTop: "4px",
            }}>
              Fill in the details below to add a new review.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.45)", transition: "color 0.15s",
              flexShrink: 0, marginTop: "2px",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "24px 28px" }}>
          <TestimonialForm
            onSave={onSave}
            onCancel={onClose}
            saving={saving}
          />
        </div>
      </div>
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────
function EditTestimonialModal({ item, onSave, onClose, saving }) {
  useEffect(() => {
    if (item) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [item]);

  if (!item) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px", backgroundColor: "rgba(1,4,20,0.5)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: "620px",
          backgroundColor: "#F7F4EF", borderRadius: "20px",
          overflow: "hidden", boxShadow: "0 32px 80px rgba(1,46,114,0.22)",
          border: "1px solid rgba(1,46,114,0.1)",
          maxHeight: "90vh", display: "flex", flexDirection: "column",
          animation: "modal-in 0.18s ease-out both",
        }}
      >
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #012E72, #002DB5)",
          padding: "22px 28px",
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", gap: "12px", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <Avatar initials={item.initials} bg={item.avatarBg} size="sm" />
            <div>
              <p style={{
                fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.28em",
                textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: "5px",
              }}>
                Edit Testimonial
              </p>
              <h2 style={{
                fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px",
                color: "#fff", letterSpacing: "0.04em", lineHeight: 1.2,
              }}>
                {item.name}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.45)", transition: "color 0.15s",
              flexShrink: 0, marginTop: "2px",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "24px 28px" }}>
          <TestimonialForm
            initial={item}
            onSave={onSave}
            onCancel={onClose}
            saving={saving}
          />
        </div>
      </div>
    </div>
  );
}

// ── Testimonial Card ──────────────────────────────────────────────────
function TestimonialCard({ t, onEdit, onDelete, onToggle, deleting, toggling }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: "#fff", borderRadius: "14px",
        padding: "16px 20px",
        display: "flex", gap: "16px", alignItems: "flex-start",
        border: `1px solid ${hovered ? "rgba(1,46,114,0.18)" : "rgba(1,46,114,0.09)"}`,
        boxShadow: hovered ? "0 4px 20px rgba(1,46,114,0.08)" : "0 1px 4px rgba(1,46,114,0.04)",
        opacity: t.isPublished ? 1 : 0.6,
        transition: "all 0.2s",
      }}
    >
      <Avatar initials={t.initials} bg={t.avatarBg} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
          <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "13px", color: "#012E72" }}>
            {t.name}
          </span>
          <Stars count={t.rating} />
          {t.timeLabel && (
            <span style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.35)", fontStyle: "italic" }}>
              {t.timeLabel}
            </span>
          )}
          {t.displayOrder !== 0 && (
            <span style={{
              fontFamily: "'Cinzel', serif", fontSize: "7px", letterSpacing: "0.14em",
              textTransform: "uppercase", color: "rgba(1,46,114,0.3)",
              border: "1px solid rgba(1,46,114,0.1)", borderRadius: "4px", padding: "2px 6px",
            }}>
              #{t.displayOrder}
            </span>
          )}
        </div>
        <p style={{
          fontFamily: "'Constantia', serif", fontSize: "13px",
          color: "rgba(1,46,114,0.5)", lineHeight: 1.7,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          "{t.text}"
        </p>
      </div>

      <div style={{
        display: "flex", alignItems: "center", gap: "8px", flexShrink: 0,
        opacity: hovered ? 1 : 0.55, transition: "opacity 0.2s",
      }}>
        {/* Live/Hidden badge */}
        <button
          onClick={() => onToggle(t)}
          disabled={toggling === t._id}
          style={{
            fontFamily: "'Cinzel', serif", fontSize: "7px", letterSpacing: "0.14em",
            textTransform: "uppercase", borderRadius: "999px", border: "none",
            padding: "6px 12px", cursor: toggling === t._id ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: "6px",
            backgroundColor: t.isPublished ? "rgba(6,95,70,0.08)" : "rgba(1,46,114,0.05)",
            color: t.isPublished ? "#065F46" : "rgba(1,46,114,0.35)",
            opacity: toggling === t._id ? 0.5 : 1, transition: "all 0.15s",
          }}
        >
          <span style={{
            width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0,
            backgroundColor: t.isPublished ? "#10B981" : "#D1D5DB",
          }} />
          {toggling === t._id ? "…" : t.isPublished ? "Live" : "Hidden"}
        </button>

        {/* Edit */}
        <button
          onClick={() => onEdit(t)}
          title="Edit"
          style={{
            width: "30px", height: "30px", borderRadius: "8px", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: "rgba(1,46,114,0.06)", color: "#012E72",
            cursor: "pointer", transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#012E72"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.06)"; e.currentTarget.style.color = "#012E72"; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>

        {/* Delete */}
        <button
          onClick={() => onDelete(t._id, t.name)}
          disabled={deleting === t._id}
          title="Delete"
          style={{
            width: "30px", height: "30px", borderRadius: "8px", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: "rgba(239,68,68,0.07)", color: "#EF4444",
            cursor: deleting === t._id ? "not-allowed" : "pointer",
            opacity: deleting === t._id ? 0.3 : 1, transition: "all 0.15s",
          }}
          onMouseEnter={e => { if (deleting !== t._id) { e.currentTarget.style.backgroundColor = "#EF4444"; e.currentTarget.style.color = "#fff"; } }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.07)"; e.currentTarget.style.color = "#EF4444"; }}
        >
          {deleting === t._id ? (
            <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          ) : (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────
export default function AdminTestimonials() {
  usePageTitle("Testimonials");
  const navigate   = useNavigate();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [showAdd,  setShowAdd]  = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [filter,   setFilter]   = useState("all");

  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });
  const [errorModal,  setErrorModal]  = useState({ open: false, message: "" });

  const handle401 = (res) => {
    if (res.status === 401) {
      localStorage.removeItem("paladin_admin_token");
      navigate("/admin/login");
      return true;
    }
    return false;
  };

  useEffect(() => {
    async function load() {
      try {
        const res = await adminFetch(API);
        if (handle401(res)) return;
        if (!res.ok) throw new Error("Failed to load testimonials.");
        const json = await res.json();
        setItems(json.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleAdd = async (form) => {
    setSaving(true);
    try {
      const res = await adminFetch(API, {
        method: "POST", body: JSON.stringify(form),
      });
      if (handle401(res)) return;
      if (!res.ok) throw new Error("Failed to save.");
      const json = await res.json();
      setItems((prev) => [json.data, ...prev]);
      setShowAdd(false);
    } catch (err) {
      setErrorModal({ open: true, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (form) => {
    setSaving(true);
    try {
      const res = await adminFetch(`${API}/${editing._id}`, {
        method: "PATCH", body: JSON.stringify(form),
      });
      if (handle401(res)) return;
      if (!res.ok) throw new Error("Failed to update.");
      const json = await res.json();
      setItems((prev) => prev.map((t) => t._id === editing._id ? json.data : t));
      setEditing(null);
    } catch (err) {
      setErrorModal({ open: true, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (item) => {
    setToggling(item._id);
    try {
      const res = await adminFetch(`${API}/${item._id}`, {
        method: "PATCH",
        body: JSON.stringify({ isPublished: !item.isPublished }),
      });
      if (!res.ok) throw new Error("Toggle failed.");
      const json = await res.json();
      setItems((prev) => prev.map((t) => t._id === item._id ? json.data : t));
    } catch (err) {
      setErrorModal({ open: true, message: err.message });
    } finally {
      setToggling(null);
    }
  };

  const promptDelete = (id, name) => {
    setDeleteModal({ open: true, id, name });
  };

  const confirmDelete = async () => {
    const { id } = deleteModal;
    setDeleteModal({ open: false, id: null, name: "" });
    setDeleting(id);
    try {
      const res = await adminFetch(`${API}/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed.");
      setItems((prev) => prev.filter((t) => t._id !== id));
      if (editing?._id === id) setEditing(null);
    } catch (err) {
      setErrorModal({ open: true, message: err.message });
    } finally {
      setDeleting(null);
    }
  };

  const published = items.filter((t) =>  t.isPublished).length;
  const hidden    = items.filter((t) => !t.isPublished).length;

  const filtered = filter === "live"   ? items.filter((t) =>  t.isPublished)
                 : filter === "hidden" ? items.filter((t) => !t.isPublished)
                 : items;

  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "240px", gap: "12px" }}>
      <p style={{ fontFamily: "'Constantia', serif", color: "#dc2626", fontSize: "14px" }}>{error}</p>
      <button onClick={() => window.location.reload()} style={{
        fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase",
        color: "#012E72", border: "1px solid rgba(1,46,114,0.25)", borderRadius: "999px",
        padding: "8px 20px", cursor: "pointer", backgroundColor: "transparent",
      }}>Retry</button>
    </div>
  );

  return (
    <>
      {/* ── Global Modals ── */}
      <ConfirmModal
        open={deleteModal.open}
        type="delete"
        title="Delete Testimonial"
        message={`Are you sure you want to delete the review by "${deleteModal.name}"? This action cannot be undone.`}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ open: false, id: null, name: "" })}
      />

      <ConfirmModal
        open={errorModal.open}
        type="error"
        title="Something Went Wrong"
        message={errorModal.message}
        confirmLabel="Dismiss"
        onConfirm={() => setErrorModal({ open: false, message: "" })}
        onCancel={() => setErrorModal({ open: false, message: "" })}
      />

      <AddTestimonialModal
        open={showAdd}
        onSave={handleAdd}
        onClose={() => setShowAdd(false)}
        saving={saving}
      />

      <EditTestimonialModal
        item={editing}
        onSave={handleEdit}
        onClose={() => setEditing(null)}
        saving={saving}
      />

      {/* ── Page Content ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "12px" }}>
          <div>
            <p style={{
              fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em",
              textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "6px",
            }}>
              Admin / Testimonials
            </p>
            <h1 style={{
              fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px",
              letterSpacing: "0.04em", color: "#012E72", lineHeight: 1,
            }}>
              Testimonials
            </h1>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            style={{
              fontFamily: "'Cinzel', serif", fontSize: "11px", letterSpacing: "0.15em",
              textTransform: "uppercase", color: "#fff",
              background: "linear-gradient(135deg, #012E72, #002DB5)",
              border: "none", borderRadius: "999px",
              padding: "12px 28px", cursor: "pointer",
              boxShadow: "0 4px 16px rgba(0,45,181,0.25)",
              display: "flex", alignItems: "center", gap: "8px",
              flexShrink: 0, paddingBottom: "4px",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Testimonial
          </button>
        </div>

        {/* ── Stat pills ── */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {[
            { label: "Total",  value: items.length, color: "#012E72", bg: "rgba(1,46,114,0.07)" },
            { label: "Live",   value: published,    color: "#065F46", bg: "#D1FAE5" },
            { label: "Hidden", value: hidden,        color: "#92400E", bg: "#FEF3C7" },
          ].map(s => (
            <div key={s.label} style={{
              backgroundColor: s.bg, borderRadius: "10px", padding: "10px 18px",
              display: "flex", alignItems: "center", gap: "10px",
            }}>
              <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: s.color }}>
                {s.value}
              </span>
              <span style={{
                fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.16em",
                textTransform: "uppercase", color: s.color, opacity: 0.7,
              }}>
                {s.label}
              </span>
            </div>
          ))}
        </div>

        {/* ── Filter pills ── */}
        {items.length > 0 && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {[
              { key: "all",    label: "All",    count: items.length },
              { key: "live",   label: "Live",   count: published },
              { key: "hidden", label: "Hidden", count: hidden },
            ].map(({ key, label, count }) => {
              const active = filter === key;
              return (
                <button key={key} onClick={() => setFilter(key)} style={{
                  fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
                  textTransform: "uppercase", padding: "8px 16px", borderRadius: "999px",
                  cursor: "pointer", transition: "all 0.15s",
                  backgroundColor: active ? "#012E72" : "#fff",
                  color:           active ? "#fff"    : "#6B7280",
                  border: `1px solid ${active ? "#012E72" : "rgba(1,46,114,0.12)"}`,
                }}>
                  {label}
                  {count > 0 && (
                    <span style={{ marginLeft: "6px", opacity: 0.65 }}>({count})</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Empty state ── */}
        {items.length === 0 ? (
          <div style={{
            backgroundColor: "#fff", borderRadius: "16px",
            border: "1px dashed rgba(1,46,114,0.15)",
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", padding: "80px 32px", gap: "14px",
          }}>
            <div style={{
              width: "52px", height: "52px", borderRadius: "50%",
              backgroundColor: "rgba(1,46,114,0.05)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="rgba(1,46,114,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{
                fontFamily: "'Cinzel', serif", fontSize: "13px",
                color: "rgba(1,46,114,0.4)", textTransform: "uppercase",
                letterSpacing: "0.06em", marginBottom: "6px",
              }}>
                No testimonials yet
              </p>
              <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.35)", fontStyle: "italic" }}>
                Add your first review to get started.
              </p>
            </div>
            <button
              onClick={() => setShowAdd(true)}
              style={{
                fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
                textTransform: "uppercase", color: "#012E72",
                border: "1px solid rgba(1,46,114,0.2)", borderRadius: "999px",
                padding: "8px 20px", cursor: "pointer", backgroundColor: "transparent",
                marginTop: "4px", transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#012E72"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#012E72"; }}
            >
              Add Testimonial
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            backgroundColor: "#fff", borderRadius: "16px",
            border: "1px solid rgba(1,46,114,0.09)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "48px 32px",
          }}>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.35)", fontStyle: "italic" }}>
              No {filter === "live" ? "live" : "hidden"} testimonials.
            </p>
          </div>
        ) : (
          <div style={{
            backgroundColor: "#fff", borderRadius: "16px", padding: "14px",
            border: "1px solid rgba(1,46,114,0.09)",
            boxShadow: "0 2px 16px rgba(1,46,114,0.06)",
            display: "flex", flexDirection: "column", gap: "8px",
          }}>
            {filtered.map((t) => (
              <TestimonialCard
                key={t._id}
                t={t}
                onEdit={(item) => setEditing(item)}
                onDelete={promptDelete}
                onToggle={togglePublished}
                deleting={deleting}
                toggling={toggling}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}